import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface RecordingStoppedEvent {
  qKey: string;
  blob: Blob | null;
}

@Injectable({ providedIn: 'root' })
export class AudioRecorderService {
  private mediaRecorder: Record<string, MediaRecorder> = {};
  private chunks: Record<string, Blob[]> = {};
  private streams: Record<string, MediaStream> = {};
  // Observable pour notifier quand un enregistrement est arrêté (avec le blob)
  private recordingStopped$ = new Subject<RecordingStoppedEvent>();

  /**
   * Observable qui émet la clé de la question et le blob quand un enregistrement est arrêté
   */
  get onRecordingStopped() {
    return this.recordingStopped$.asObservable();
  }

  async startRecording(qKey: string): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.streams[qKey] = stream;
    this.mediaRecorder[qKey] = new MediaRecorder(stream);
    this.chunks[qKey] = [];
    this.mediaRecorder[qKey].ondataavailable = (e) => {
      if (this.chunks[qKey] && e.data && e.data.size > 0) {
        this.chunks[qKey].push(e.data);
      }
    };
    this.mediaRecorder[qKey].start();
  }

  stopRecording(qKey: string): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      if (!this.mediaRecorder[qKey] || this.mediaRecorder[qKey].state === 'inactive') {
        resolve(null as unknown as Blob);
        return;
      }
      this.mediaRecorder[qKey].onstop = () => {
        const blob = new Blob(this.chunks[qKey], { type: 'audio/webm' });
        // Nettoyer les ressources
        if (this.streams[qKey]) {
          this.streams[qKey].getTracks().forEach(track => track.stop());
          delete this.streams[qKey];
        }
        delete this.mediaRecorder[qKey];
        delete this.chunks[qKey];
        resolve(blob);
      };

      // Gérer les erreurs potentielles
      this.mediaRecorder[qKey].onerror = (error) => {
        delete this.mediaRecorder[qKey];
        delete this.chunks[qKey];
        reject(error);
      };

      if (this.mediaRecorder[qKey].state === 'recording') {
        this.mediaRecorder[qKey].stop();
      } else {
        // Si l'enregistrement n'est pas en cours, nettoyer et rejeter
        delete this.mediaRecorder[qKey];
        delete this.chunks[qKey];
        reject(new Error(`Recording is not in recording state for key: ${qKey}`));
      }
    });
  }

  /**
   * Vérifie si un enregistrement est en cours pour une question
   */
  isRecording(qKey: string): boolean {
    return this.mediaRecorder[qKey]?.state === 'recording';
  }

  /**
   * Arrête l'enregistrement en cours pour une question et retourne le blob
   */
  stopRecordingIfActive(qKey: string): Promise<Blob | null> {
    return new Promise<Blob | null>((resolve) => {
      if (!this.isRecording(qKey)) {
        resolve(null);
        return;
      }

      const recorder = this.mediaRecorder[qKey];
      const stream = this.streams[qKey];
      const chunks = this.chunks[qKey];

      // Configurer le handler onstop pour récupérer le blob
      recorder.onstop = () => {
        const blob = chunks && chunks.length > 0 
          ? new Blob(chunks, { type: 'audio/webm' }) 
          : null;

        // Nettoyer les ressources
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          delete this.streams[qKey];
        }
        delete this.mediaRecorder[qKey];
        delete this.chunks[qKey];

        // Notifier que l'enregistrement a été arrêté avec le blob
        this.recordingStopped$.next({ qKey, blob });

        resolve(blob);
      };

      // Gérer les erreurs potentielles
      recorder.onerror = (error) => {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          delete this.streams[qKey];
        }
        delete this.mediaRecorder[qKey];
        delete this.chunks[qKey];
        this.recordingStopped$.next({ qKey, blob: null });
        resolve(null);
      };

      // Arrêter l'enregistrement
      if (recorder.state === 'recording') {
        recorder.stop();
      } else {
        // Si l'enregistrement n'est pas en cours, nettoyer et résoudre avec null
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          delete this.streams[qKey];
        }
        delete this.mediaRecorder[qKey];
        delete this.chunks[qKey];
        this.recordingStopped$.next({ qKey, blob: null });
        resolve(null);
      }
    });
  }

  /**
   * Récupère toutes les questions qui ont un enregistrement en cours
   */
  getQuestionsWithRecordingInProgress(): string[] {
    return Object.entries(this.mediaRecorder)
      .filter(([_, recorder]) => recorder?.state === 'recording')
      .map(([qKey, _]) => qKey);
  }
}
